/**
 * mockBank — front-end-only mock of a paid question-bank store (MONEY model).
 *
 * ⚠️  THIS IS A PRESENTATION MOCK. There is NO real payment and NO backend.
 * Everything (folder prices, purchases, teacher grants) lives in localStorage
 * so it survives refreshes and is shared across tabs. Exams stay free — only
 * question-bank "folders" (topics) are gated here.
 *
 * Model (per the latest spec):
 *   • Each folder (topic) has a fixed PRICE IN MONEY set by the teacher
 *     when creating/editing the folder. No points, no wallet.
 *   • A student unlocks a folder by paying its FULL price once (mock card).
 *   • The teacher can also OPEN a folder for a specific student for free
 *     (grant), even if the student never paid.
 *
 * A folder is UNLOCKED for a student when EITHER it was purchased OR granted.
 *
 * Usage:
 *   import { useMockBank } from '../lib/mockBank';
 *   const bank = useMockBank();
 *   bank.getPrice(topicId);                      // number (currency)
 *   bank.isUnlocked(studentId, topicId);
 *   bank.purchaseTopic(studentId, topicId, t);   // mark as paid (mock)
 *   bank.setPrice(topicId, 10);                  // teacher
 *   bank.setGrant(studentId, topicId, true);     // teacher opens for student
 */
import { useSyncExternalStore, useCallback } from 'react';

const STORAGE_KEY = 'mock_bank_v2';
const EVENT_NAME = 'mock-bank-change';

export const DEFAULT_PRICE = 5;      // default folder price (mock money)
export const CURRENCY = 'د.أ';        // Jordanian dinar label used across the app

const EMPTY_STATE = {
  prices: {},     // { [topicId]: number }  — money amount
  purchases: {},  // { [studentId]: { [topicId]: { amount, ts } } }
  grants: {},     // { [studentId]: { [topicId]: true } }
};

function readState() {
  if (typeof window === 'undefined') return { ...EMPTY_STATE };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_STATE };
    return { ...EMPTY_STATE, ...JSON.parse(raw) };
  } catch {
    return { ...EMPTY_STATE };
  }
}

// Stable cached snapshot so useSyncExternalStore doesn't loop.
let cache = readState();
let cacheRaw = typeof window !== 'undefined'
  ? window.localStorage.getItem(STORAGE_KEY)
  : null;

function getSnapshot() {
  if (typeof window === 'undefined') return cache;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw !== cacheRaw) {
    cacheRaw = raw;
    cache = readState();
  }
  return cache;
}

function writeState(next) {
  cache = next;
  cacheRaw = JSON.stringify(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, cacheRaw);
  } catch { /* ignore quota / private-mode errors */ }
  window.dispatchEvent(new Event(EVENT_NAME));
}

function structuredCloneSafe(obj) {
  try {
    return structuredClone(obj);
  } catch {
    return JSON.parse(JSON.stringify(obj));
  }
}

function update(mutator) {
  const draft = structuredCloneSafe(getSnapshot());
  mutator(draft);
  writeState(draft);
  return draft;
}

function subscribe(callback) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVENT_NAME, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT_NAME, callback);
    window.removeEventListener('storage', callback);
  };
}

// ── Pure selectors ────────────────────────────────────────────────────────────

export function selectPrice(state, topicId) {
  const p = state.prices?.[topicId];
  return Number.isFinite(p) ? p : DEFAULT_PRICE;
}

export function selectPurchased(state, studentId, topicId) {
  return Boolean(state.purchases?.[studentId]?.[topicId]);
}

export function selectGranted(state, studentId, topicId) {
  return Boolean(state.grants?.[studentId]?.[topicId]);
}

export function selectUnlocked(state, studentId, topicId) {
  return selectPurchased(state, studentId, topicId) || selectGranted(state, studentId, topicId);
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export const bankActions = {
  /** Teacher sets the money price of a folder. */
  setPrice(topicId, price) {
    const value = Math.max(0, Number(price) || 0);
    update((s) => { s.prices[topicId] = value; });
  },

  /** Student pays the full price of a folder (mock — no real money moves). */
  purchaseTopic(studentId, topicId) {
    if (!studentId || !topicId) return { ok: false };
    const snap = getSnapshot();
    if (selectUnlocked(snap, studentId, topicId)) return { ok: true, already: true };
    const price = selectPrice(snap, topicId);
    update((s) => {
      if (!s.purchases[studentId]) s.purchases[studentId] = {};
      s.purchases[studentId][topicId] = { amount: price, ts: Date.now() };
    });
    return { ok: true, price };
  },

  /** Teacher opens (grants) / closes a folder for a specific student. */
  setGrant(studentId, topicId, granted) {
    update((s) => {
      if (!s.grants[studentId]) s.grants[studentId] = {};
      if (granted) s.grants[studentId][topicId] = true;
      else delete s.grants[studentId][topicId];
    });
  },

  /**
   * Bulk open/close: grant (or revoke) every topic in `topicIds` for every
   * student in `studentIds` in a single update. Used by the teacher panel to
   * unlock multiple folders for multiple students (or all students) at once.
   * Purchased folders are left untouched on revoke.
   */
  setGrantBulk(studentIds, topicIds, granted) {
    update((s) => {
      for (const sid of studentIds) {
        if (!s.grants[sid]) s.grants[sid] = {};
        for (const tid of topicIds) {
          if (granted) s.grants[sid][tid] = true;
          else delete s.grants[sid][tid];
        }
      }
    });
  },
};

// ── React hook ────────────────────────────────────────────────────────────────

export function useMockBank() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const getPrice = useCallback((topicId) => selectPrice(state, topicId), [state]);
  const isUnlocked = useCallback((studentId, topicId) => selectUnlocked(state, studentId, topicId), [state]);
  const isPurchased = useCallback((studentId, topicId) => selectPurchased(state, studentId, topicId), [state]);
  const isGranted = useCallback((studentId, topicId) => selectGranted(state, studentId, topicId), [state]);

  const purchaseTopic = useCallback(
    (studentId, topicId) => bankActions.purchaseTopic(studentId, topicId),
    [],
  );

  return {
    state,
    getPrice,
    isUnlocked,
    isPurchased,
    isGranted,
    purchaseTopic,
    setPrice: bankActions.setPrice,
    setGrant: bankActions.setGrant,
    setGrantBulk: bankActions.setGrantBulk,
  };
}
